import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, User } from 'lucide-react';
import { Loader } from '../../components/ui';
import { ProfileMenu } from '../profile/ProfileMenu';
import type { User as AuthUser } from '@supabase/supabase-js';
import { getUserAvatarUrl, getUserDisplayName } from '../../lib/user-utils';
import { isCapacitorAvailable } from '../../capacitor-utils';
import { useViewport } from '../../contexts/ViewportContext';

interface SidebarFooterProps {
  user: AuthUser | null;
  loading: boolean;
  isMenuOpen: boolean;
  onMenuToggle: (isOpen: boolean) => void;
}

export function SidebarFooter({ user, loading, isMenuOpen, onMenuToggle }: SidebarFooterProps) {
  const displayName = getUserDisplayName(user);
  const avatarUrl = getUserAvatarUrl(user);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isCapacitor, setIsCapacitor] = useState(false);
  const { isMobile } = useViewport();

  // Check if we're in a Capacitor app
  useEffect(() => {
    setIsCapacitor(isCapacitorAvailable());
  }, []);

  // Handle toggle click - simplified to be more direct
  const handleToggle = () => {
    console.log('Toggle clicked, current state:', isMenuOpen, 'changing to:', !isMenuOpen);
    onMenuToggle(!isMenuOpen);
  };

  return (
    <div
      className={`SidebarFooter ${isMobile ? 'sticky bottom-0 z-50' : ''} bg-[var(--sidebar-bg-color)]`}
    >
      <div className={`relative p-3 pr-4 h-[72px] flex items-center ${isCapacitor ? 'pb-8' : ''}`}>
        <button
          ref={buttonRef}
          onClick={handleToggle}
          className="w-full flex items-center gap-3 rounded-lg group cursor-pointer"
        >
          <div className="w-10 h-10 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden shadow">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User size={20} className="text-gray-600" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center">
                <Loader size="small" color="text-gray-400" className="mr-2" />
                <span className="text-base text-gray-400">Loading...</span>
              </div>
            ) : (
              <div className="text-left">
                <div className="text-base font-medium text-gray-700 truncate">
                  {displayName || 'Username'}
                </div>
              </div>
            )}
          </div>
          {isMenuOpen ? (
            <ChevronDown size={18} className="text-gray-400 flex-shrink-0 ml-1" />
          ) : (
            <ChevronUp size={18} className="text-gray-400 flex-shrink-0 ml-1" />
          )}
        </button>
        <ProfileMenu
          isOpen={isMenuOpen}
          onClose={() => onMenuToggle(false)}
          triggerRef={buttonRef}
        />
      </div>
    </div>
  );
}
